import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { Calendar, Search } from 'lucide-react';
import { useAtendimentos } from '@/hooks/useAtendimentos';
import { usePaciente } from '@/hooks/usePaciente';
import { AtendimentoCard } from '@/components/atendimento/AtendimentoCard';
import { LoadingState } from '@/components/shared/LoadingState';
import { ErrorState } from '@/components/shared/ErrorState';
import { EmptyState } from '@/components/shared/EmptyState';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useState } from 'react';

const SearchSchema = z.object({
  cd_paciente: z.number().int().positive().optional(),
});

export const Route = createFileRoute('/pep-vida/_authed/atendimentos/')({
  validateSearch: SearchSchema,
  component: AtendimentosListPage,
});

function AtendimentosListPage() {
  const navigate = useNavigate();
  const { cd_paciente: cdFromUrl } = Route.useSearch();
  const [inputValue, setInputValue] = useState<string>(
    String(cdFromUrl ?? 125),
  );

  const cdPaciente = cdFromUrl ?? 125;
  const {
    data: consultas,
    isLoading,
    error,
    refetch,
  } = useAtendimentos(cdPaciente);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = Number(inputValue);
    if (Number.isInteger(parsed) && parsed > 0) {
      navigate({
        to: '/pep-vida/atendimentos',
        search: { cd_paciente: parsed },
      });
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Search className="h-4 w-4" />
            Buscar paciente
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-3 sm:flex-row sm:items-end"
          >
            <div className="flex-1 space-y-1.5">
              <Label htmlFor="cd" className="text-xs">
                Código do paciente (cd_paciente)
              </Label>
              <Input
                id="cd"
                type="number"
                min={1}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
              />
            </div>
            <Button type="submit">Buscar</Button>
          </form>
        </CardContent>
      </Card>

      {isLoading && <LoadingState variant="skeleton" />}
      {error && (
        <ErrorState
          message={(error as Error).message}
          onRetry={() => refetch()}
        />
      )}
      {consultas && consultas.length === 0 && (
        <EmptyState
          title="Nenhum atendimento"
          description={`Não há consultas para o paciente ${cdPaciente}.`}
          icon={<Calendar className="h-6 w-6" />}
        />
      )}
      {consultas && consultas.length > 0 && (
        <div className="space-y-2" data-testid="atendimentos-list">
          {consultas.map((c) => (
            <AtendimentoRow
              key={c.cdAtendimento}
              consulta={c}
              cdPaciente={cdPaciente}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AtendimentoRow({
  consulta,
  cdPaciente,
}: {
  consulta: import('@/types/api').ClinusAtendimento;
  cdPaciente: number;
}) {
  const { data: paciente } = usePaciente(cdPaciente);
  return <AtendimentoCard consulta={consulta} paciente={paciente} />;
}
